import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Gem, Check } from "lucide-react";

export default function Credits() {
  const { user, refreshUser } = useAuth();
  const t = useT();
  usePageMeta(t("cred.meta_title"));
  const [packages, setPackages] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/credits/packages");
      const rows = Object.entries(data || {})
        .filter(([, v]) => v.mode === "payment")
        .map(([key, v]) => ({ key, ...v }))
        .sort((a, b) => a.amount - b.amount);
      setPackages(rows);
    } catch { setPackages([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const buy = async (pkg) => {
    setBusy(pkg.key);
    try {
      const { data } = await api.post("/credits/checkout", { package: pkg.key, origin_url: window.location.href });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data?.mock) { toast.success(t("cred.mock_granted", { n: data.granted_credits })); await refreshUser?.(); }
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const perCredit = (p) => eur(Math.round(p.amount / p.credits));

  return (
    <div data-testid="coach-credits-page">
      <Seo title={t("cred.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cred-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("cred.eyebrow")} title={t("cred.title")} subtitle={t("cred.subtitle")} />

      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="cred-balance">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Gem className="h-6 w-6" /></div>
        <div>
          <p className="font-display text-3xl text-foreground">{user?.credits ?? 0}</p>
          <p className="text-xs text-muted-foreground">{t("cred.balance")}</p>
        </div>
      </div>

      {packages === null ? (
        <div className="grid gap-3 sm:grid-cols-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {packages.map((p) => (
            <div key={p.key} data-testid={`cred-pkg-${p.key}`} className="flex flex-col rounded-2xl border border-border bg-card p-5">
              <div className="flex items-baseline justify-between">
                <p className="font-display text-2xl text-foreground">{p.credits}</p>
                <p className="text-sm font-medium text-primary">{eur(p.amount)}</p>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("cred.per_credit", { price: perCredit(p) })}</p>
              <Button className="mt-4 w-full gap-1.5 rounded-full" disabled={busy === p.key} onClick={() => buy(p)} data-testid={`cred-buy-${p.key}`}>
                <Check className="h-4 w-4" /> {busy === p.key ? t("common.loading") : t("cred.buy")}
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-center text-xs text-muted-foreground">{t("cred.vat_note")}</p>
    </div>
  );
}
